import { transformOfferDto } from '@marketplace-web/offers/price-offers-data'
import { transformUserBundleDiscountDto } from '@marketplace-web/bundles/items-bundle-data'
import { transformPackageSizeDto } from '@marketplace-web/shipping/package-size-data'
import { transformCurrencyAmountDto } from '@marketplace-web/currency/currency-data'

import { transformShipmentDto } from './shipment'
import {
  TransactionDto,
  TransactionItemDto,
  TransactionItemModel,
  TransactionModel,
  TransactionOrderDto,
  TransactionOrderModel,
  TransactionUserDto,
  TransactionUserModel,
  TransactionUserPhoto,
} from '../types/transaction'
import { AdditionalTransactionAction } from '../constants'
import { findThumbnail } from '../utils/photo'

enum ItemThumbnailSize {
  Medium = 'thumb150x210',
  Large = 'thumb310x430',
}

const getItemThumbnail = (
  photos: Array<{
    is_main: boolean
    thumbnails: Array<{ type: ItemThumbnailSize | string; url: string }>
  }>,
  size = ItemThumbnailSize.Medium,
) => {
  const photo = photos?.find(({ is_main }) => is_main)

  return findThumbnail(photo, size)
}

const transformUserPhotoDto = (photoDto: TransactionUserPhoto): TransactionUserPhoto => ({
  url: photoDto.url,
  thumbnails: photoDto.thumbnails,
})

const transformUserDto = (user: TransactionUserDto): TransactionUserModel => ({
  id: user.id,
  bundleDiscount: user.bundle_discount
    ? transformUserBundleDiscountDto(user.bundle_discount)
    : null,
  business: user.business,
  closetCountdownEndDate: user.closet_countdown_end_date,
  login: user.login,
  photo: user?.photo ? transformUserPhotoDto(user.photo) : null,
})

const transformTransactionItemDto = (item: TransactionItemDto): TransactionItemModel => ({
  id: item.id,
  title: item.title,
  size: item.size_title || '',
  status: item.status,
  price: transformCurrencyAmountDto(item.price),
  thumbnailUrl: getItemThumbnail(item.photos),
  url: item.url,
  photos: item.photos.map(photo => ({
    url: photo.url,
    thumbnails: photo.thumbnails,
    isMain: photo.is_main,
    imageNo: photo.image_no,
  })),
})

export const transformTransactionItemDtos = (
  items: Array<TransactionItemDto>,
): Array<TransactionItemModel> => items.map(transformTransactionItemDto)

export const transformOrder = (order: TransactionOrderDto): TransactionOrderModel => ({
  items: transformTransactionItemDtos(order.items),
  itemIds: order.item_ids,
  title: order.title,
  reserved: order.reserved,
  packageSize: order.package_size ? transformPackageSizeDto(order.package_size) : null,
})

export const transformTransaction = ({
  id,
  buyer,
  order,
  shipment,
  available_actions,
  debit_processed_at,
  user_msg_thread_id,
  items_count,
  item_id,
  offer,
  status,
  seller,
  status_title,
  status_updated_at,
  user_side,
  item_title,
  suspending_complaint_id,
  offline_verification,
  total_amount_without_tax,
  service_fee,
}: TransactionDto): TransactionModel => ({
  id,
  order: transformOrder(order),
  buyer: transformUserDto(buyer),
  userSide: user_side,
  shipment: shipment && transformShipmentDto(shipment),
  canMarkAsReceived: available_actions.includes(AdditionalTransactionAction.MarkAsReceived),
  isDebitProcessed: !!debit_processed_at,
  conversationId: user_msg_thread_id,
  availableActions: available_actions,
  itemsCount: items_count,
  itemId: item_id,
  offer: transformOfferDto(offer),
  status,
  seller: seller ? transformUserDto(seller) : null,
  statusTitle: status_title,
  statusUpdatedAt: status_updated_at,
  itemTitle: item_title,
  complaintId: suspending_complaint_id ? suspending_complaint_id.toString() : null,
  offlineVerification: offline_verification,
  totalAmountWithoutTax: transformCurrencyAmountDto(total_amount_without_tax),
  serviceFee: transformCurrencyAmountDto(service_fee),
})

export const transformTransactions = (
  transactions: Array<TransactionDto>,
): Array<TransactionModel> => transactions.map(transformTransaction)
